import {
  Autocomplete,
  Box,
  Divider,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import * as dayjs from "dayjs";
import { useEffect, useState } from "react";
import api from "../../../../services/api";
import { useStyle } from "./styles";
import { useOutletContext } from "react-router-dom";
interface AgendamentoDataType {
  id?: string | null;
  dataInicial: Date | null;
  tipoColeta: string;
  ocorrencias: number | null;
  intervaloColeta: number | null;
  title?: string;
  perfil?: PerfilDataType[];
  todosRelatoriosNaoGerados?: boolean;
  quantidadeGerados?: number;
  relatoriosGeradosDataUltrapassada?: boolean;
}

interface IntervaloColetaProps {
  agendamentoData: AgendamentoDataType;
  setAgendamentoData: React.Dispatch<React.SetStateAction<AgendamentoDataType>>;
  isEditing?: boolean;
}
interface OutletContextType {
  config: EasyReportConfig[];
  setConfig: React.Dispatch<React.SetStateAction<EasyReportConfig[]>>;
}

export const AgendamentoConfig: React.FC<IntervaloColetaProps> = ({
  agendamentoData,
  setAgendamentoData,
  isEditing,
}) => {
  const theme = useTheme();
  const styles = useStyle(theme);
  const [timeLimit, setTimeLimit] = useState(2);
  const [errorDate, setErrorDate] = useState<string | null>(null);

  async function handleGetSettings() {
    try {
      const response = await api.get("/settings/findLimited");
      const easyReportSetting = response.data.find(
        (p: { produto: string }) => p.produto === "easyreport",
      );
      if (easyReportSetting && easyReportSetting.configuracoes?.limiteTempo) {
        setTimeLimit(easyReportSetting.configuracoes.limiteTempo);
      }
    } catch {}
  }
  const { config } = useOutletContext<OutletContextType>();

  useEffect(() => {
    const intervalConfig = config.find((c) => c.nome === "geral")?.configuracoes
      ?.interval;
    setTimeLimit(intervalConfig || 2);
  }, [config]);

  const getLimitInterval = (value: string | null = null) => {
    if (value ? value === "Dias" : agendamentoData.tipoColeta === "day") {
      return 60;
    }
    if (value ? value === "Semanas" : agendamentoData.tipoColeta === "week") {
      return 8;
    }
    if (value ? value === "Meses" : agendamentoData.tipoColeta === "month") {
      return 2;
    }
    return 60;
  };

  const handleIntervalNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/[^0-9]/g, "");

    const max = getLimitInterval();
    if (val === "0") {
      val = "1";
    }
    if (val && Number(val) > max) {
      val = String(max);
    }
    setAgendamentoData((prev) => ({
      ...prev,
      intervaloColeta: val === "" ? null : Number(val),
    }));
  };

  const handleSelectChange = (
    event: React.SyntheticEvent,
    newValue: { label: string; value: string } | null,
  ) => {
    const selectedValue = newValue ? newValue.value : "";
    const selectedLabel = newValue ? newValue.label : "";

    setAgendamentoData((prev) => ({
      ...prev,
      tipoColeta: selectedValue,
    }));

    const limit = getLimitInterval(selectedLabel);

    setAgendamentoData((prev) => {
      if (prev.intervaloColeta && prev.intervaloColeta > limit) {
        return { ...prev, intervaloColeta: limit };
      }
      return prev;
    });
  };

  const handleOccurrenceNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/[^0-9]/g, "");
    if (val === "0") {
      val = "1";
    }
    if (parseInt(val) > 120) {
      val = "120";
    }
    setAgendamentoData((prev) => ({
      ...prev,
      ocorrencias: val === "" ? null : Number(val),
    }));
  };
  const options = [
    { label: "Dias", value: "day" },
    { label: "Semanas", value: "week" },
    { label: "Meses", value: "month" },
  ];

  const getExampleDates = () => {
    if (
      !agendamentoData.dataInicial ||
      !agendamentoData.intervaloColeta ||
      !agendamentoData.tipoColeta ||
      !agendamentoData.ocorrencias
    ) {
      return [];
    }
    const result = [];
    let currentStart = dayjs(agendamentoData.dataInicial);
    const interval = agendamentoData.intervaloColeta;
    const type = agendamentoData.tipoColeta;

    for (let i = 0; i < Math.min(agendamentoData.ocorrencias, 5); i++) {
      let currentEnd;
      currentEnd = currentStart.add(interval, type as "day" | "week" | "month");

      let endPeriod = currentEnd;
      if (type === "day") endPeriod = currentEnd.subtract(1, "day");

      result.push(
        `${currentStart.format("DD/MM/YYYY HH:mm")} > ${endPeriod?.format("DD/MM/YYYY HH:mm")}`,
      );

      currentStart = currentEnd!;
    }
    return result;
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>{"Agendamento"}</Typography>
        <Typography sx={styles.subtitle}>
          {"Defina a frequência de geração do relatório"}
        </Typography>
      </Box>
      <Divider />
      <Box sx={styles.boxIntervalo}>
        <Box sx={styles.boxInput}>
          <Typography sx={styles.titleInput}>
            {"Data Inicial de Coleta"}
          </Typography>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={"pt-br"}
          >
            <DateTimePicker
              value={
                agendamentoData.dataInicial
                  ? dayjs(agendamentoData.dataInicial)
                  : null
              }
              onChange={(newValeu: dayjs.Dayjs | null) => {
                if (newValeu)
                  setAgendamentoData((prev) => ({
                    ...prev,
                    dataInicial: dayjs(newValeu).toDate(),
                  }));
              }}
              onError={(newError) => setErrorDate(newError)}
              slotProps={{
                textField: {
                  size: "small",
                  helperText:
                    !isEditing && errorDate === "minDate"
                      ? `A data inicial não pode ser anterior a ${timeLimit} meses atrás`
                      : "",
                },
              }}
              minDate={dayjs().subtract(timeLimit, "month")}
              disabled={
                isEditing && agendamentoData.todosRelatoriosNaoGerados !== true
              }
            />
          </LocalizationProvider>
        </Box>
        <Box sx={styles.boxInput}>
          <Typography sx={styles.titleInput}>Intervalo de coleta</Typography>
          <Box sx={styles.boxColeta}>
            <TextField
              sx={{ width: "10%" }}
              value={agendamentoData.intervaloColeta ?? ""}
              size="small"
              slotProps={{
                htmlInput: {
                  maxLength: 2,
                  "aria-label": "Intervalo",
                },
              }}
              onChange={handleIntervalNumber}
              disabled={isEditing}
            />
            <Autocomplete
              value={
                options.find(
                  (opt) => opt.value === agendamentoData.tipoColeta,
                ) || null
              }
              fullWidth
              options={options}
              getOptionLabel={(option) => option.label}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  inputProps={{ ...params.inputProps, "aria-label": "Unidade" }}
                />
              )}
              onChange={handleSelectChange}
              disabled={isEditing}
            />
          </Box>
        </Box>
        <Box sx={styles.boxInput}>
          <Typography sx={styles.titleInput}>Ocorrências</Typography>
          <TextField
            sx={{ width: "100%" }}
            value={agendamentoData.ocorrencias ?? ""}
            size="small"
            inputProps={{
              maxLength: 3,
            }}
            error={
              Number(agendamentoData.ocorrencias) <
              Number(agendamentoData.quantidadeGerados)
            }
            helperText={
              Number(agendamentoData.ocorrencias) <
                Number(agendamentoData.quantidadeGerados) &&
              "A quantidade de ocorrências é inferior a quantidade de relatórios gerados!"
            }
            onInput={handleOccurrenceNumber}
          />
        </Box>
        {agendamentoData.dataInicial &&
          agendamentoData.intervaloColeta &&
          agendamentoData.tipoColeta &&
          agendamentoData.ocorrencias && (
            <Box sx={styles.boxInput}>
              <Box>
                <Typography sx={styles.titleInput}>
                  Exemplo de Intervalo
                </Typography>
                <Box
                  sx={{
                    marginTop: 1,
                    p: 1,
                    backgroundColor: theme.palette.action.hover,
                    borderRadius: 1,
                  }}
                >
                  {getExampleDates()
                    .slice(0, 3)
                    .map((dateRange, index) => (
                      <Typography key={index} variant="caption" display="block">
                        {dateRange}
                      </Typography>
                    ))}
                  {(agendamentoData.ocorrencias > 3 ||
                    getExampleDates().length > 3) && (
                    <Typography variant="caption" display="block">
                      ...
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
      </Box>
    </Box>
  );
};
